import Reveal from '@/components/feature/Reveal';
import ZoomableImage from '@/components/feature/ZoomableImage';

/**
 * A framed "receipt" for the Performance page — a screenshot slot styled like a
 * terminal / statement window so it reads as raw output rather than site chrome.
 *
 * To publish a real screenshot: drop the file into `public/` (e.g.
 * `public/proof/mt5-statement.png`) and pass its path through `image`. Until
 * `image` is set, the frame shows a clearly labelled placeholder slot instead of
 * an empty box or, worse, a fabricated screenshot.
 *
 * When `image` is set the frame becomes clickable: tapping it opens a full-screen
 * lightbox with zoom controls, so dense lists stay readable.
 */
export interface ProofShotProps {
  /** Window title shown in the frame's top bar, e.g. "MT5 · History". */
  title: string;
  /** Optional right-hand status pill, e.g. "account #12345678" or "verified". */
  status?: string;
  /** Icon class for the status pill. */
  statusIcon?: string;
  /** Public path to the real screenshot once it exists. Empty shows the slot. */
  image?: string;
  /** Accessible description of the screenshot (used as the alt text). */
  alt: string;
  /** Caption rendered under the frame explaining what the visitor is looking at. */
  caption: string;
  /** Short label describing what belongs in this slot (shown while empty). */
  slotLabel: string;
  /**
   * When the source is longer than one screen, show an honest note over the
   * bottom edge so the visitor knows they are seeing an excerpt, not the whole
   * document. Leave empty when the screenshot is complete.
   */
  excerptNote?: string;
  delay?: number;
}

export default function ProofShot({
  title,
  status,
  statusIcon = 'ri-shield-check-line',
  image,
  alt,
  caption,
  slotLabel,
  excerptNote,
  delay = 0,
}: ProofShotProps) {
  return (
    <Reveal delay={delay} className="h-full">
      <figure className="flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50">
        {/* Window title bar */}
        <div className="flex items-center gap-3 border-b border-background-200 bg-background-100 px-4 py-2.5">
          <span className="flex items-center gap-1.5" aria-hidden="true">
            <span className="h-2.5 w-2.5 rounded-full bg-accent-300"></span>
            <span className="h-2.5 w-2.5 rounded-full bg-secondary-300"></span>
            <span className="h-2.5 w-2.5 rounded-full bg-primary-300"></span>
          </span>
          <span className="truncate font-num text-xs font-semibold text-foreground-700">{title}</span>
          {status ? (
            <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-2 py-0.5 sm:py-1 text-[10px] sm:text-[11px] font-semibold text-primary-700 max-w-[45%] truncate">
              <i className={`${statusIcon} shrink-0`} aria-hidden="true"></i>
              <span className="truncate">{status}</span>
            </span>
          ) : null}
        </div>

        {/* Screenshot area — fixed ratio so the slot never collapses */}
        <div className="relative aspect-[16/10] w-full bg-background-100">
          <ZoomableImage
            src={image}
            alt={alt}
            title={title}
            className="absolute inset-0 h-full w-full"
            imageClassName="h-full w-full object-cover object-top"
            empty={
              <div className="absolute inset-3 flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-background-300 bg-background-50/70 px-6 text-center">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-image-add-line text-xl" aria-hidden="true"></i>
                </span>
                <p className="text-xs font-semibold text-foreground-800 md:text-sm">{slotLabel}</p>
                <p className="text-[11px] leading-relaxed text-foreground-500">
                  Drop the real file into <span className="font-num">/public</span> and point this slot at
                  its path.
                </p>
              </div>
            }
          />

          {/* Excerpt marker — tells the visitor this is a slice of a longer source */}
          {excerptNote ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center bg-gradient-to-t from-background-50 via-background-50/85 to-transparent px-3 pt-10 pb-2.5">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-background-200 bg-background-50 px-2.5 py-1 text-[10px] font-semibold text-foreground-600">
                <i className="ri-scissors-cut-line" aria-hidden="true"></i>
                {excerptNote}
              </span>
            </div>
          ) : null}
        </div>

        <figcaption className="border-t border-background-200 px-4 py-3 text-xs leading-relaxed text-foreground-600">
          {caption}
        </figcaption>
      </figure>
    </Reveal>
  );
}